import { useCallback, useEffect, useState } from 'react';
import { Activity, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Drawer } from './Drawer';
import { Button } from './Button';
import { EmptyState } from './EmptyState';
import { Select } from '@/components/forms/Select';
import {
  listAuditoria,
  listAuditoriaFacetas,
  type AuditoriaRow,
  type AuditoriaFacet,
} from '@/services/api/auditoria';
import { cn } from '@/lib/cn';

interface Props {
  open: boolean;
  onClose: () => void;
  empresaId: string;
}

const LIMIT = 100;

/**
 * Visor del audit log (tabla `auditoria_cambios`). Accesible para APEX y
 * PARTNER (la RLS garantiza que no lea nada de otras empresas). Lista las
 * últimas 100 operaciones con filtro por entidad / operación; expandir
 * cada fila muestra el diff (before/after según como lo grabe el trigger).
 */
export function AuditLogDrawer({ open, onClose, empresaId }: Props) {
  const [rows, setRows] = useState<AuditoriaRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState<AuditoriaFacet[]>([]);
  const [entidadFiltro, setEntidadFiltro] = useState('');
  const [operFiltro, setOperFiltro] = useState('');
  const [expandido, setExpandido] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    const res = await listAuditoria({
      empresaId,
      entidad: entidadFiltro || undefined,
      operacion: operFiltro || undefined,
      limit: LIMIT,
    });
    setLoading(false);
    if (!res.ok) {
      toast.error(res.error.message);
      return;
    }
    setRows(res.data.rows);
    setTotal(res.data.total);
  }, [empresaId, entidadFiltro, operFiltro]);

  useEffect(() => {
    if (!open) return;
    void reload();
    void listAuditoriaFacetas(empresaId).then((r) => {
      if (r.ok) setFacets(r.data);
    });
  }, [open, reload, empresaId]);

  // Dropdown de entidades únicas (ordenadas por frecuencia).
  const entidades = Array.from(new Set(facets.map((f) => f.entidad))).sort();
  // Dropdown de operaciones: filtrada por entidad actual si aplica.
  const operaciones = Array.from(
    new Set(
      facets
        .filter((f) => !entidadFiltro || f.entidad === entidadFiltro)
        .map((f) => f.operacion),
    ),
  ).sort();

  function toggleExpand(id: string) {
    setExpandido((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={720}
      kicker="Auditoría"
      title="Registro de cambios"
      description="Últimas operaciones sobre las entidades principales. Solo visible para APEX y PARTNER."
      footer={<Button onClick={onClose}>Cerrar</Button>}
    >
      <div className="space-y-4">
        {/* Filtros */}
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[160px] flex-1">
            <label className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wider text-neutral-muted">
              Entidad
            </label>
            <Select
              value={entidadFiltro}
              onChange={(e) => {
                setEntidadFiltro(e.target.value);
                setOperFiltro('');
              }}
            >
              <option value="">Todas</option>
              {entidades.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-[160px] flex-1">
            <label className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wider text-neutral-muted">
              Operación
            </label>
            <Select value={operFiltro} onChange={(e) => setOperFiltro(e.target.value)}>
              <option value="">Todas</option>
              {operaciones.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </Select>
          </div>
          {(entidadFiltro || operFiltro) && (
            <Button
              variant="secondary"
              onClick={() => {
                setEntidadFiltro('');
                setOperFiltro('');
              }}
            >
              Limpiar
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-xs text-neutral-muted">
          <span>
            {loading ? (
              'Cargando…'
            ) : rows ? (
              <>
                Mostrando {rows.length} de <b className="text-neutral-ink">{total}</b>{' '}
                cambios
                {rows.length < total && ' (primeras filas)'}
              </>
            ) : null}
          </span>
          <span className="flex items-center gap-1 text-[11px]">
            <Activity size={12} /> total {facets.reduce((a, f) => a + f.cnt, 0)} en toda
            la empresa
          </span>
        </div>

        {/* Listado */}
        {loading && !rows ? (
          <div className="grid place-items-center py-10 text-sm text-neutral-muted">
            Cargando…
          </div>
        ) : !rows || rows.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="Sin registros"
            description="No hay cambios registrados con estos filtros."
          />
        ) : (
          <ul className="space-y-1">
            {rows.map((r) => {
              const isOpen = expandido.has(r.id);
              return (
                <li
                  key={r.id}
                  className="overflow-hidden rounded-md border border-surface-line bg-white"
                >
                  <button
                    type="button"
                    onClick={() => toggleExpand(r.id)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left transition hover:bg-surface-bg/50"
                  >
                    {isOpen ? (
                      <ChevronDown size={14} className="flex-none text-neutral-muted" />
                    ) : (
                      <ChevronRight size={14} className="flex-none text-neutral-muted" />
                    )}
                    <OperacionBadge op={r.operacion} />
                    <span className="flex-none rounded bg-blue-10/50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-blue">
                      {r.entidad}
                    </span>
                    <span className="flex-1 truncate text-xs text-neutral-ink">
                      {r.user_nombre ?? 'Sistema'}
                      {r.entidad_id && (
                        <span className="ml-2 font-mono text-[10px] text-neutral-muted">
                          #{r.entidad_id.slice(0, 8)}
                        </span>
                      )}
                    </span>
                    <span
                      className="flex-none text-[10px] text-neutral-muted"
                      title={r.created_at}
                    >
                      {formatFechaRelativa(r.created_at)}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="border-t border-surface-line/60 bg-surface-bg/30 p-3">
                      {r.diff ? (
                        <pre className="max-h-[300px] overflow-auto rounded bg-white p-2 font-mono text-[10px] text-neutral-ink">
                          {JSON.stringify(r.diff, null, 2)}
                        </pre>
                      ) : (
                        <p className="text-[11px] italic text-neutral-muted">
                          Sin diff guardado (operación sin cambios materiales o anterior a
                          la auditoría).
                        </p>
                      )}
                      <p className="mt-2 text-[10px] text-neutral-muted">
                        Exacto: {new Date(r.created_at).toLocaleString('es-AR')} ·
                        {r.user_id ? ` user ${r.user_id.slice(0, 8)}` : ' sin user'}
                      </p>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Drawer>
  );
}

function OperacionBadge({ op }: { op: string }) {
  const tone =
    op === 'INSERT' || op === 'insert' || op === 'create'
      ? 'bg-status-success/15 text-status-success'
      : op === 'DELETE' || op === 'delete'
        ? 'bg-status-danger/15 text-status-danger'
        : op === 'UPDATE' || op === 'update'
          ? 'bg-orange/15 text-orange'
          : 'bg-neutral-muted/15 text-neutral-muted';
  return (
    <span
      className={cn(
        'flex-none rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase',
        tone,
      )}
    >
      {op}
    </span>
  );
}

function formatFechaRelativa(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'ahora';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
}
